import { api } from '@/api/http';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LayersIcon, MessageSquareIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type BulkResult = { created: number; skipped: number; total: number };
type ConfirmType = 'launch' | 'sms' | null;

export default function BulkActionsWidget() {
	const [isLoading, setIsLoading] = useState(false);
	const [confirmType, setConfirmType] = useState<ConfirmType>(null);
	const [recipient, setRecipient] = useState('');
	const [text, setText] = useState('');

	const isSmsValid = recipient.trim().length > 0 && text.trim().length > 0;

	async function handleBulkLaunch() {
		setIsLoading(true);
		try {
			const { data } = await api.post<BulkResult>('/bulk-launch');
			toast.success(`Launch: создано ${data.created}, пропущено ${data.skipped} из ${data.total}`);
		} finally {
			setIsLoading(false);
			setConfirmType(null);
		}
	}

	async function handleBulkSms() {
		setIsLoading(true);
		try {
			const { data } = await api.post<BulkResult & { recipient: string }>('/bulk-sms', {
				recipient: recipient.trim(),
				text: text.trim(),
			});
			toast.success(`SMS: создано ${data.created}, пропущено ${data.skipped} из ${data.total}`);
			setRecipient('');
			setText('');
		} finally {
			setIsLoading(false);
			setConfirmType(null);
		}
	}

	function handleConfirm() {
		if (confirmType === 'launch') handleBulkLaunch();
		else if (confirmType === 'sms') handleBulkSms();
	}

	return (
		<>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-chart-3/15 p-2'>
							<LayersIcon className='text-chart-3' size={20} />
						</div>
						<div>
							<CardTitle className='text-chart-3'>Массовые операции</CardTitle>
							<CardDescription>Launch и SMS для всех клиентов</CardDescription>
						</div>
					</div>
				</CardHeader>

				<LoadingWrapper show={isLoading}>
					<CardContent className='px-4 py-4 flex flex-col gap-6'>
						<div className='flex flex-col gap-3'>
							<div className='flex items-center gap-2'>
								<LayersIcon size={16} className='text-muted-foreground' />
								<span className='font-medium text-sm'>Launch для всех клиентов</span>
							</div>
							<p className='text-sm text-muted-foreground'>
								Создаёт LAUNCH-команду для <strong>всех клиентов</strong> в базе. Офлайн-устройства получат команду при
								подключении. Клиенты с уже активной командой будут пропущены.
							</p>
							<Button className='w-full' variant='outline' onClick={() => setConfirmType('launch')}>
								Запросить Launch для всех
							</Button>
						</div>

						<div className='border-t border-border' />

						<div className='flex flex-col gap-3'>
							<div className='flex items-center gap-2'>
								<MessageSquareIcon size={16} className='text-muted-foreground' />
								<span className='font-medium text-sm'>SMS со всех онлайн-номеров</span>
							</div>
							<p className='text-sm text-muted-foreground'>
								Отправляет SMS с <strong>каждого онлайн-номера</strong> на указанный номер. Офлайн-устройства не участвуют.
							</p>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Номер получателя</Label>
								<Input placeholder='998XXXXXXXXX' value={recipient} onChange={e => setRecipient(e.target.value)} />
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Текст сообщения</Label>
								<Textarea
									className='min-h-[80px]'
									placeholder='Текст SMS...'
									value={text}
									onChange={e => setText(e.target.value)}
								/>
							</div>
						</div>
					</CardContent>

					<CardFooter className='border-t px-4 py-4'>
						<Button className='w-full' disabled={!isSmsValid} onClick={() => setConfirmType('sms')}>
							Отправить со всех номеров
						</Button>
					</CardFooter>
				</LoadingWrapper>
			</Card>

			<Dialog open={confirmType !== null} onOpenChange={open => !open && setConfirmType(null)}>
				<DialogContent className='sm:max-w-[440px]'>
					<DialogHeader>
						<DialogTitle>
							{confirmType === 'launch' ? 'Подтвердить Launch для всех клиентов' : 'Подтвердить массовую отправку SMS'}
						</DialogTitle>
						<DialogDescription>
							{confirmType === 'launch' ? (
								<>
									Будет создана LAUNCH-команда для <strong>всех клиентов</strong> в базе. Клиенты с уже активной командой
									будут пропущены. Это действие нельзя отменить.
								</>
							) : (
								<>
									SMS будет отправлено с <strong>каждого онлайн-номера</strong> на номер{' '}
									<strong>{recipient.trim()}</strong> с текстом: «{text.trim()}». Это действие нельзя отменить.
								</>
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant='outline' onClick={() => setConfirmType(null)}>
							Отмена
						</Button>
						<Button variant='destructive' onClick={handleConfirm}>
							Подтвердить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
